import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { HOSPITAL_CLAIM_ENABLED } from './featureFlags';
import { getHospitalAvatar, PinIcon, CheckIcon } from './hospitalVisuals';
import './VetHospitalDetail.css';

const SERVICE_LABELS = [
    ['officeVisit', 'Office visit'],
    ['vaccinations', 'Vaccinations'],
    ['bloodTests', 'Blood tests'],
    ['xrays', 'X-rays'],
    ['urinalysis', 'Urinalysis'],
    ['spayNeuter', 'Spay / neuter'],
    ['fleaTickHeartworm', 'Flea / tick / heartworm']
];

const formatLocation = (cityRegion) => {
    const value = String(cityRegion || '').trim();
    return /^city\s*\d+(\s*,\s*[a-z]{2})?$/i.test(value) ? 'Location not listed' : value || 'Location not listed';
};

const formatDocumentType = (documentType) => {
    const value = String(documentType || 'upload').trim().toLowerCase();
    if (value === 'bill') return 'Bill';
    if (value === 'estimate') return 'Estimate';
    return 'Upload';
};

const getServiceConfidence = (service) => {
    if (!service || Array.isArray(service)) {
        return null;
    }

    const billCount = Number(service.billCount || 0);
    const estimateCount = Number(service.estimateCount || 0);

    if (billCount > 0 && estimateCount > 0) {
        return { label: 'Bills + estimates', type: 'mixed' };
    }

    if (billCount > 0) {
        return { label: 'From a paid bill', type: 'bill' };
    }

    if (estimateCount > 0) {
        return { label: 'From an estimate', type: 'estimate' };
    }

    return null;
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const getServiceSearchText = (service) => [
    service.description,
    service.standardization?.canonicalService,
    service.standardization?.category,
    service.normalized?.unit,
    ...(service.tags || [])
].map(normalizeText).join(' ');

const getServicePrice = (service) => {
    const price = service.normalized?.actualCost
        ?? service.normalized?.total
        ?? service.normalized?.lowEstimate
        ?? service.totalCost
        ?? service.lowCost
        ?? service.highCost;

    const numericPrice = Number(price);
    return Number.isFinite(numericPrice) ? `$${numericPrice.toFixed(2)}` : 'No price found';
};

const formatAggregatedPrice = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? `$${numericValue.toFixed(2)}` : 'Not enough data';
};

const getMatchingServices = (bill, selectedService) => {
    if (!selectedService) {
        return [];
    }

    const selectedText = normalizeText(selectedService);
    return (bill.services || []).filter(service => getServiceSearchText(service).includes(selectedText));
};

const getTopServices = (bill, count = 3) => {
    const services = Array.isArray(bill.services) ? bill.services : [];

    return services
        .map(service => service.standardization?.canonicalService || service.description)
        .filter(Boolean)
        .slice(0, count);
};

const formatRange = (service) => {
    if (!service) {
        return null;
    }

    const low = Number(service.low);
    const high = Number(service.high);

    if (!Number.isFinite(low) || !Number.isFinite(high)) {
        return null;
    }

    return {
        type: 'price',
        value: low === high ? `$${low.toFixed(2)}` : `$${low.toFixed(2)} - $${high.toFixed(2)}`,
        confidence: getServiceConfidence(service)
    };
};

const VetHospitalDetail = () => {
    const { hospitalName } = useParams();
    const navigate = useNavigate();
    const [hospitalData, setHospitalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImage, setCurrentImage] = useState('');
    const [showAllExtractedServices, setShowAllExtractedServices] = useState(false);
    const [documentFilter, setDocumentFilter] = useState('all');
    const [selectedExtractedService, setSelectedExtractedService] = useState('');

    useEffect(() => {
        const fetchHospitalData = async () => {
            try {
                const response = await fetch(`/api/hospitals/${encodeURIComponent(hospitalName)}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setHospitalData(data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching hospital data:', error);
                setError('Failed to load hospital data');
                setLoading(false);
            }
        };

        fetchHospitalData();
    }, [hospitalName]);

    if (loading) {
        return <div className="hospital-detail-container">Loading...</div>;
    }

    if (error) {
        return <div className="hospital-detail-container">{error}</div>;
    }

    const comparableServices = SERVICE_LABELS
        .map(([key, label]) => ({ key, label, summary: formatRange(hospitalData.services?.[key]) }))
        .filter(service => service.summary);
    const medicationServices = Array.isArray(hospitalData.services?.commonMedications)
        ? hospitalData.services.commonMedications
        : [];
    const extractedServices = [...new Set([...medicationServices, ...(hospitalData.otherServices || [])])];
    const visibleExtractedServices = showAllExtractedServices ? extractedServices : extractedServices.slice(0, 18);
    const hiddenExtractedServiceCount = extractedServices.length - visibleExtractedServices.length;
    const totalUploads = (hospitalData.numberOfBills || 0) + (hospitalData.numberOfEstimates || 0);
    const uploadDates = (hospitalData.bills || [])
        .map(bill => new Date(bill.billDate).getTime())
        .filter(timestamp => Number.isFinite(timestamp));
    const lastUpdated = uploadDates.length ? new Date(Math.max(...uploadDates)) : null;
    const filteredBills = (hospitalData.bills || []).filter((bill) => {
        const normalizedDocumentType = normalizeText(bill.documentType);
        const matchesDocumentType = documentFilter === 'all' || normalizedDocumentType === documentFilter;
        const matchesService = !selectedExtractedService || getMatchingServices(bill, selectedExtractedService).length > 0;

        return matchesDocumentType && matchesService;
    });
    const selectedServiceMatchCount = selectedExtractedService
        ? (hospitalData.bills || []).filter(bill => getMatchingServices(bill, selectedExtractedService).length > 0).length
        : 0;
    const billCount = (hospitalData.bills || []).filter(bill => normalizeText(bill.documentType) === 'bill').length;
    const estimateCount = (hospitalData.bills || []).filter(bill => normalizeText(bill.documentType) === 'estimate').length;
    const dataDepthLabel = comparableServices.length >= 3
        ? 'Good price coverage'
        : comparableServices.length > 0
            ? 'More uploads will improve this profile'
            : 'Needs pricing uploads';
    const avatar = getHospitalAvatar(hospitalData.vetHospital);
    const hasRealBills = (hospitalData.numberOfBills || 0) > 0;
    const estimatesOnly = !hasRealBills && (hospitalData.numberOfEstimates || 0) > 0;
    const servicePricing = Array.isArray(hospitalData.servicePricing) ? hospitalData.servicePricing : [];
    const servicePricingByCategory = servicePricing.reduce((groups, item) => {
        const category = item.category || 'Other';
        if (!groups.has(category)) groups.set(category, []);
        groups.get(category).push(item);
        return groups;
    }, new Map());
    const describeEvidence = (item) => {
        const lines = `${item.count} upload${item.count === 1 ? '' : 's'}`;
        if (item.billCount > 0 && item.estimateCount > 0) return `${lines} · bills + estimates`;
        if (item.billCount > 0) return `${lines} · from paid bills`;
        return `${lines} · from estimates`;
    };
    const describeRange = (item) => {
        if (item.min === item.max) return formatAggregatedPrice(item.min);
        return `${formatAggregatedPrice(item.min)} – ${formatAggregatedPrice(item.max)}`;
    };

    return (
        <div className="hospital-detail-container">
            <div className="hospital-inner">
            <button className="back-button" onClick={() => navigate('/browse')}>Back to results</button>

            <section className="hospital-profile-hero">
                <span className="eyebrow-label">Hospital pricing profile</span>
                <div className="hospital-hero-top">
                    <span className="hospital-avatar hospital-avatar-lg" style={{ background: avatar.color }} aria-hidden="true">{avatar.initials}</span>
                    <div className="hospital-detail-header">
                        <h2>{hospitalData.vetHospital}</h2>
                    </div>
                </div>

                <p className="hospital-hero-location">
                    <PinIcon />
                    {formatLocation(hospitalData.cityRegion)}{lastUpdated && ` · Updated ${lastUpdated.toLocaleDateString()}`}
                </p>
                <div className="hospital-hero-badges">
                    {hasRealBills ? (
                        <span className="hcard-badge badge-bills"><CheckIcon /> From real bills</span>
                    ) : estimatesOnly ? (
                        <span className="hcard-badge badge-estimates">Estimates only</span>
                    ) : null}
                    {hospitalData.verifiedProfile && (
                        <span className="hcard-badge badge-verified"><CheckIcon /> Verified profile</span>
                    )}
                </div>

                <div className="profile-snapshot-grid">
                    <div>
                        <span>Prices to compare</span>
                        <strong>{comparableServices.length}</strong>
                    </div>
                    <div>
                        <span>Total uploads</span>
                        <strong>{totalUploads}</strong>
                    </div>
                    <div>
                        <span>Bills</span>
                        <strong>{hospitalData.numberOfBills || 0}</strong>
                    </div>
                    <div>
                        <span>Estimates</span>
                        <strong>{hospitalData.numberOfEstimates || 0}</strong>
                    </div>
                </div>
            </section>

            {hospitalData.verifiedProfile && (
                <section className="hospital-section verified-profile-section">
                    <div className="verified-profile-header">
                        <div>
                            <h3>Hospital-provided information</h3>
                            <p className="section-help">This area is separate from community-uploaded pricing.</p>
                        </div>
                        <span className="verified-badge">Verified by admin</span>
                    </div>
                    <div className="verified-profile-grid">
                        {hospitalData.verifiedProfile.phoneNumber && (
                            <div>
                                <span>Phone</span>
                                <strong>{hospitalData.verifiedProfile.phoneNumber}</strong>
                            </div>
                        )}
                        {hospitalData.verifiedProfile.website && (
                            <div>
                                <span>Website</span>
                                <a href={hospitalData.verifiedProfile.website} target="_blank" rel="noreferrer">{hospitalData.verifiedProfile.website}</a>
                            </div>
                        )}
                        {hospitalData.verifiedProfile.appointmentUrl && (
                            <div>
                                <span>Appointments</span>
                                <a href={hospitalData.verifiedProfile.appointmentUrl} target="_blank" rel="noreferrer">Book or request appointment</a>
                            </div>
                        )}
                        {hospitalData.verifiedProfile.hours && (
                            <div>
                                <span>Hours</span>
                                <strong>{hospitalData.verifiedProfile.hours}</strong>
                            </div>
                        )}
                        {hospitalData.verifiedProfile.address && (
                            <div className="wide">
                                <span>Address</span>
                                <strong>
                                    {[hospitalData.verifiedProfile.address, hospitalData.verifiedProfile.city, hospitalData.verifiedProfile.state, hospitalData.verifiedProfile.zipCode]
                                        .filter(Boolean)
                                        .join(', ')}
                                </strong>
                            </div>
                        )}
                        {hospitalData.verifiedProfile.servicesOffered && (
                            <div className="wide">
                                <span>Services listed by hospital</span>
                                <strong>{hospitalData.verifiedProfile.servicesOffered}</strong>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <section className="hospital-section">
                <div className="section-title-row">
                    <div>
                        <h3>Prices you can compare</h3>
                        <p className="section-help">Standardized services from uploaded bills and estimates.</p>
                    </div>
                    <div className="section-title-badges">
                        {comparableServices.length > 0 && (
                            <span className="section-count">{comparableServices.length} prices</span>
                        )}
                        <span className="depth-badge">{dataDepthLabel}</span>
                    </div>
                </div>
                {comparableServices.length ? (
                    <div className="pricing-grid">
                        {comparableServices.map(service => (
                            <div key={service.key} className="pricing-card">
                                <div>
                                    <span>{service.label}</span>
                                    <strong>{service.summary.value}</strong>
                                </div>
                                {service.summary.confidence && (
                                    <small className={`confidence-badge ${service.summary.confidence.type}`}>
                                        {service.summary.confidence.label}
                                    </small>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="empty-documents">
                        No prices to compare yet. More uploads from this hospital will fill this in.
                    </div>
                )}
            </section>

            {servicePricing.length > 0 && (
                <section className="hospital-section">
                    <div className="section-title-row">
                        <div>
                            <h3>Typical Prices by Service</h3>
                            <p className="section-help">Each price describes one specific service from uploaded bills and estimates — ranges are never mixed across different services.</p>
                        </div>
                        <span className="section-count">{servicePricing.length} services</span>
                    </div>
                    <div className="pricing-table-wrap">
                        <table className="pricing-table service-pricing-table">
                            <thead>
                                <tr>
                                    <th>Service</th>
                                    <th>Typical</th>
                                    <th>Seen from</th>
                                    <th>Based on</th>
                                </tr>
                            </thead>
                            {Array.from(servicePricingByCategory.entries()).map(([category, items]) => (
                                <tbody key={category}>
                                    <tr className="pricing-category-row">
                                        <th colSpan="4">{category}</th>
                                    </tr>
                                    {items.map(item => (
                                        <tr key={`${category}-${item.service}`}>
                                            <td className="pricing-service-name">{item.service}</td>
                                            <td className="pricing-typical">{formatAggregatedPrice(item.median)}</td>
                                            <td>{describeRange(item)}</td>
                                            <td>{describeEvidence(item)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            ))}
                        </table>
                    </div>
                    <p className="pricing-disclaimer">
                        Pricing is aggregated from community uploads and may not reflect current rates.
                    </p>
                </section>
            )}

            {extractedServices.length > 0 && (
                <details className="hospital-section secondary-services-section">
                    <summary>
                        <span>
                            <strong>Other services seen</strong>
                            <small>Seen on uploads here, but not enough yet to compare prices.</small>
                        </span>
                        <em>{extractedServices.length} services</em>
                    </summary>
                    <div className="tag-list extracted-services-list">
                        {visibleExtractedServices.map(service => (
                            <button
                                key={service}
                                type="button"
                                className={`tag service-filter-chip ${selectedExtractedService === service ? 'active' : ''}`}
                                onClick={() => setSelectedExtractedService(selectedExtractedService === service ? '' : service)}
                            >
                                {service}
                            </button>
                        ))}
                        {hiddenExtractedServiceCount > 0 && (
                            <button
                                type="button"
                                className="show-more-tags"
                                onClick={() => setShowAllExtractedServices(true)}
                            >
                                +{hiddenExtractedServiceCount} more
                            </button>
                        )}
                        {showAllExtractedServices && extractedServices.length > 18 && (
                            <button
                                type="button"
                                className="show-more-tags"
                                onClick={() => setShowAllExtractedServices(false)}
                            >
                                Show fewer
                            </button>
                        )}
                    </div>
                </details>
            )}

            <section className="hospital-section">
                <div className="documents-header">
                    <div>
                        <h3>Uploaded Evidence</h3>
                        <p>
                            Showing {filteredBills.length} of {hospitalData.bills.length} uploads
                            {selectedExtractedService && ` mentioning ${selectedExtractedService}`}
                        </p>
                    </div>
                    {(selectedExtractedService || documentFilter !== 'all') && (
                        <button
                            type="button"
                            className="clear-filters-button"
                            onClick={() => {
                                setSelectedExtractedService('');
                                setDocumentFilter('all');
                            }}
                        >
                            Clear filters
                        </button>
                    )}
                </div>

                <div className="document-filter-bar" aria-label="Filter uploaded documents">
                    <button
                        type="button"
                        className={documentFilter === 'all' ? 'active' : ''}
                        onClick={() => setDocumentFilter('all')}
                    >
                        All uploads <span>{hospitalData.bills.length}</span>
                    </button>
                    <button
                        type="button"
                        className={documentFilter === 'bill' ? 'active' : ''}
                        onClick={() => setDocumentFilter('bill')}
                    >
                        Bills <span>{billCount}</span>
                    </button>
                    <button
                        type="button"
                        className={documentFilter === 'estimate' ? 'active' : ''}
                        onClick={() => setDocumentFilter('estimate')}
                    >
                        Estimates <span>{estimateCount}</span>
                    </button>
                </div>

                {selectedExtractedService && (
                    <div className="selected-service-filter">
                        <span>{selectedServiceMatchCount} uploads mention</span>
                        <strong>{selectedExtractedService}</strong>
                    </div>
                )}

                <div className="vet-bills-grid">
                    {filteredBills.map((bill) => {
                        const documentType = formatDocumentType(bill.documentType);
                        const matchingServices = getMatchingServices(bill, selectedExtractedService);
                        const topServices = getTopServices(bill);

                        return (
                            <div key={bill.id || bill.imageUrl} className="vet-bill-card clickable-document-card">
                                <Link
                                    to={`/bills/${bill.id}`}
                                    className="document-card-link"
                                    aria-label={`Open ${documentType.toLowerCase()} details from ${new Date(bill.billDate).toLocaleDateString()}`}
                                />
                                <div className="bill-image-wrap">
                                    <img src={bill.imageUrl} alt={`${documentType} thumbnail`} />
                                    <button
                                        type="button"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setCurrentImage(bill.imageUrl);
                                        }}
                                        className="preview-image-button"
                                    >
                                        Preview image
                                    </button>
                                </div>
                                <div className="vet-bill-info">
                                    <div className="document-card-header">
                                        <span className="document-type-badge">{documentType}</span>
                                        <span>{new Date(bill.billDate).toLocaleDateString()}</span>
                                    </div>
                                    <p>{bill.services?.length || 0} services</p>
                                    {!selectedExtractedService && topServices.length > 0 && (
                                        <div className="document-service-preview">
                                            <span>Top services</span>
                                            <strong>{topServices.join(', ')}</strong>
                                        </div>
                                    )}
                                    {selectedExtractedService && matchingServices.length > 0 && (
                                        <div className="matching-services">
                                            {matchingServices.slice(0, 3).map(service => (
                                                <div key={service._id || `${bill.id}-${service.description}`} className="matching-service-line">
                                                    <span>{service.description}</span>
                                                    <strong>{getServicePrice(service)}</strong>
                                                </div>
                                            ))}
                                            {matchingServices.length > 3 && (
                                                <small>+{matchingServices.length - 3} more matching lines</small>
                                            )}
                                        </div>
                                    )}
                                    <p className="open-details-text">Open upload</p>
                                    {bill.tags?.length > 0 && (
                                        <div className="tag-list document-tags">
                                            {bill.tags.slice(0, 5).map((tag) => <span key={tag} className="tag">{tag}</span>)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {!filteredBills.length && (
                    <div className="empty-documents">
                        No uploads match these filters yet.
                    </div>
                )}
            </section>

            {HOSPITAL_CLAIM_ENABLED && !hospitalData.verifiedProfile && (
                <section className="hospital-claim-footer">
                    <span>Work at this hospital?</span>
                    <Link to={`/hospital-claim?hospital=${encodeURIComponent(hospitalData.vetHospital)}`}>
                        Request to claim this profile
                    </Link>
                </section>
            )}

            </div>{/* .hospital-inner */}

            {currentImage && (
                <div className="custom-lightbox" onClick={() => setCurrentImage('')}>
                    <img src={currentImage} alt="Uploaded vet document" onClick={e => e.stopPropagation()} />
                    <button className="close-button" onClick={() => setCurrentImage('')}>Close</button>
                </div>
            )}
        </div>
    );
};

export default VetHospitalDetail;
